import { useEffect, useState } from 'react';

/**
 * Tailwind breakpoints.
 *
 * @see styles/index.css
 */
const breakpoints = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;

type Breakpoint = keyof typeof breakpoints;

/**
 * Custom hook to check if a media query matches
 * @param query - Media query string (e.g., '(min-width: 768px)')
 * @returns boolean indicating if the media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }

    // Fallback for older browsers
    else {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  return matches;
};

/**
 * Helper to create min-width media query
 */
export const minWidth = (breakpoint: Breakpoint): string =>
  `(min-width: ${breakpoints[breakpoint]}px)`;

/**
 * Helper to create max-width media query
 */
export const maxWidth = (breakpoint: Breakpoint): string =>
  `(max-width: ${breakpoints[breakpoint] - 1}px)`;

/**
 * Helper to create between media query
 */
export const between = (
  minBreakpoint: Breakpoint,
  maxBreakpoint: Breakpoint,
): string =>
  `(min-width: ${breakpoints[minBreakpoint]}px) and (max-width: ${breakpoints[maxBreakpoint] - 1}px)`;

/**
 * Convenience hook for checking if screen is at least a certain breakpoint
 * @example
 * const isMdAndUp = useBreakpointUp('md'); // true if >= 768px
 */
export const useBreakpointUp = (breakpoint: Breakpoint): boolean =>
  useMediaQuery(minWidth(breakpoint));

/**
 * Convenience hook for checking if screen is below a certain breakpoint
 * @example
 * const isSmAndDown = useBreakpointDown('sm'); // true if < 576px
 */
export const useBreakpointDown = (breakpoint: Breakpoint): boolean =>
  useMediaQuery(maxWidth(breakpoint));

/**
 * Convenience hook for checking if screen is between two breakpoints
 * @example
 * const isBetweenMdAndLg = useBreakpointBetween('md', 'lg'); // true if >= 768px and < 992px
 */
export const useBreakpointBetween = (
  minBreakpoint: Breakpoint,
  maxBreakpoint: Breakpoint,
): boolean => useMediaQuery(between(minBreakpoint, maxBreakpoint));
